import { init, getInstance, IAssignmentLogger } from '@eppo/js-client-sdk';
import windowWrapper from '../window_wrapper';
import { mParticleReady } from '../elog/mparticle';
import { EPPO_API_KEY } from '@reverbdotcom/env';
import { EventType } from '../elog/mparticle_types';
import * as elog from '../elog';
import { UserContextExperiment, getExperimentInt } from '../user_context_helpers';
import { IUser } from '../components/user_context_provider';

export const EPPO_EXPERIMENT_NAMES = {
  WEB_CHECKOUT_TRUSTPILOT_V2: 'web_checkout_trustpilot_v2',
} as const;
export type EppoExpName = typeof EPPO_EXPERIMENT_NAMES[keyof typeof EPPO_EXPERIMENT_NAMES];

export const EPPO_ASSIGNMENT_GROUPS = {
  /**
   * The entity (user) was not assigned to 'control' or 'treatment'.
   * From a UI perspective this should be handled the same as 'control',
   * but assignment events will not be logged, and qualifying events
   * should not be tagged with a group for this experiment.
   */
  FALLBACK: 'fallback',
  CONTROL: 'control',
  TREATMENT: 'treatment',
} as const;
export type EppoAssignmentGroup = typeof EPPO_ASSIGNMENT_GROUPS[keyof typeof EPPO_ASSIGNMENT_GROUPS];

/**
 * Stores cached experiment assignments. Eppo says:
 * > We recommend wrapping the SDK code in a useMemo hook to avoid invoking the assignment logic on every render.
 *
 * This isn't using useMemo but should be an equivalent per-experiment assignment cache.
 */
export const cachedExpData = {};

/**
 * This function is temporary and exists to run a client-side only AB experiment while Eppo is being evaluated.
 * For a more detailed description of a future state of supporting non-rollout experiments,
 * see [Reverb RFC-14](https://reverb.atlassian.net/wiki/spaces/ENG/pages/2089779205/RFC+14+-+Supporting+Non-Rollout+Experiments+in+Frontend).
 */
export function formatEppoExperimentsForEventAttributes(expNames: EppoExpName[], cache = cachedExpData, user = eppoUser): string {
  return expNames.map((expName) => {
    const variant = getVariant(expName, cache, user);

    if (variant === EPPO_ASSIGNMENT_GROUPS.CONTROL) {
      const exp: UserContextExperiment = { value: 'default' };
      return `${expName}:${getExperimentInt(exp)}`;
    }

    if (variant === EPPO_ASSIGNMENT_GROUPS.TREATMENT) {
      const exp: UserContextExperiment = { value: 'experiment' };
      return `${expName}:${getExperimentInt(exp)}`;
    }

    // EPPO_ASSIGNMENT_GROUPS.FALLBACK
    return '';
  }).join('|');
}

/**
 * This function is temporary and exists to run a client-side only AB experiment while Eppo is being evaluated.
 * For a more detailed description of a future state of supporting non-rollout experiments,
 * see [Reverb RFC-14](https://reverb.atlassian.net/wiki/spaces/ENG/pages/2089779205/RFC+14+-+Supporting+Non-Rollout+Experiments+in+Frontend).
 */
export const useEppoExpEnabled = (expName: EppoExpName, cache = cachedExpData, user = eppoUser): boolean => {
  return getVariant(expName, cache, user) === EPPO_ASSIGNMENT_GROUPS.TREATMENT;
};

const getVariant = (expName: EppoExpName, cache = cachedExpData, user = eppoUser): string => {
  if (cache[expName]) {
    return cache[expName];
  }

  const variant = getInstance().getStringAssignment(
    expName,
    user.subjectKey,
    user.subjectAttributes,
    EPPO_ASSIGNMENT_GROUPS.FALLBACK,
  );
  cache[expName] = variant;

  return variant;
};

/**
 * Based on https://docs.geteppo.com/sdks/event-logging/#examples-for-common-logging-systems
 * but adapted for mParticle's client SDK instead of the mParticle server SDK shown in the example.
 */
const assignmentLogger: IAssignmentLogger = {
  logAssignment(assignment) {
    mParticleReady(
      () => {
        const formattedAssignment = {
          ...assignment,
          shippingRegionCode: assignment.subjectAttributes.shippingRegionCode,
        };
        delete formattedAssignment.metaData;
        delete formattedAssignment.subjectAttributes;
        windowWrapper.mParticle.logEvent(
          'Eppo Randomization Event',
          EventType.Navigation,
          formattedAssignment,
        );
      },
    );
  },
};


interface EppoUser {
  subjectKey: string;
  subjectAttributes: Record<string, string>;
}

/**
 * Singleton representing the current user for Eppo SDK purposes.
 */
let eppoUser: EppoUser = {
  subjectKey: '',
  subjectAttributes: {},
};

let isEppoReady = false;

export async function initializeEppoSDK(user: IUser) {
  if (isEppoReady) {
    return;
  }

  eppoUser = {
    /**
    * Each Eppo experiment is setup with an Entity for assignment.
    * Reverb's configuration has been set up with 2 entities for now: User and Cookie.
    * For POC web experiments, assume Cookie is always the entity type.
    */
    subjectKey: user.cookieId,

    /**
    * Other per-user attributes could be added here and
    * used with more sophisticated assignment targeting rules.
    */
    subjectAttributes: {
      shippingRegionCode: user.shippingRegionCode,
    },
  };

  try {
    await init({
      apiKey: EPPO_API_KEY,
      assignmentLogger,
    });

    isEppoReady = true;
  } catch (ex) {
    elog.error('eppo.error.init', {}, ex);
  }
}
